import { useState, useRef, useEffect, useMemo } from "react";

interface SearchableSelectProps {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  dropUp?: boolean;
}

export default function SearchableSelect({
  value,
  options,
  onChange,
  placeholder = "Select...",
  className = "",
  dropUp = false,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const lower = search.toLowerCase();
    return options.filter(opt => opt.label.toLowerCase().includes(lower));
  }, [options, search]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (optValue: string) => {
    onChange(optValue);
    setIsOpen(false);
    setSearch("");
  };

  const handleInputClick = () => {
    setIsOpen(true);
    setSearch("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      setSearch("");
    } else if (e.key === "Enter" && filteredOptions.length === 1) {
      handleSelect(filteredOptions[0].value);
    }
  };

  return (
    <div ref={containerRef} className={`searchable-select ${className}`}>
      <div className="searchable-select__trigger" onClick={handleInputClick}>
        {isOpen ? (
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={selectedOption?.label || placeholder}
            className="searchable-select__input"
            autoFocus
          />
        ) : (
          <span
            className={`searchable-select__value${!selectedOption ? " searchable-select__value--placeholder" : ""}`}
          >
            {selectedOption?.label || placeholder}
          </span>
        )}
        <span className="searchable-select__arrow">▾</span>
      </div>
      {isOpen && (
        <ul className={`searchable-select__dropdown${dropUp ? " searchable-select__dropdown--up" : ""}`}>
          {filteredOptions.length === 0 ? (
            <li className="searchable-select__option searchable-select__option--empty">No matches</li>
          ) : (
            filteredOptions.map(opt => (
              <li
                key={opt.value}
                className={`searchable-select__option${opt.value === value ? " searchable-select__option--selected" : ""}`}
                onClick={() => handleSelect(opt.value)}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
